<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { ChevronDown, ChevronUp, Loader2, FileText } from 'lucide-vue-next'
import { getSessionReports } from '@/services/chatApi'

const props = defineProps({
  sessionUuid: {
    type: String,
    required: true
  },
  organizationId: {
    type: Number,
    required: true
  },
  isGenerating: {
    type: Boolean,
    default: false
  }
})

const router = useRouter()
const route = useRoute()

const isExpanded = ref(false)
const isLoading = ref(false)
const reports = ref([])
const error = ref(null)

// Watch for generation completion to refresh reports
// When isGenerating goes from true to false, a message stream completed
watch(() => props.isGenerating, (newVal, oldVal) => {
  if (oldVal === true && newVal === false) {
    // Stream just completed - refresh reports in case a new one was created
    fetchReports()
  }
})

onMounted(() => {
  // Fetch reports on mount
  if (props.sessionUuid) {
    fetchReports()
  }
})

const reportsCount = computed(() => reports.value.length)
const activeReportUuid = computed(() => route.query.report)

const fetchReports = async () => {
  if (!props.sessionUuid) return

  isLoading.value = true
  error.value = null

  try {
    const response = await getSessionReports(props.organizationId, props.sessionUuid)
    reports.value = response.reports || []
  } catch (err) {
    console.error('Error fetching session reports:', err)
    error.value = err.message || 'Failed to load reports'
  } finally {
    isLoading.value = false
  }
}

const goToReport = (reportUuid) => {
  router.push({
    name: 'ChatSession',
    params: {
      org_id: props.organizationId,
      session_code: route.params.session_code
    },
    query: {
      report: reportUuid
    }
  })
}

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value
  if (isExpanded.value && reports.value.length === 0) {
    fetchReports()
  }
}

// Watch for session changes
watch(() => props.sessionUuid, (newUuid) => {
  if (newUuid) {
    reports.value = []
    fetchReports()
  }
})
</script>

<template>
  <div class="border-t border-border bg-background">
    <!-- Header -->
    <button
      @click="toggleExpand"
      class="w-full px-3 py-2 flex items-center justify-between hover:bg-muted/80 transition-colors"
    >
      <div class="flex items-center gap-2">
        <span class="text-sm font-medium text-foreground">Reports</span>
        <span
          v-if="reportsCount > 0"
          class="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full"
        >
          {{ reportsCount }}
        </span>
      </div>
      <div class="flex items-center gap-1">
        <ChevronUp v-if="isExpanded" class="h-4 w-4 text-muted-foreground" />
        <ChevronDown v-else class="h-4 w-4 text-muted-foreground" />
      </div>
    </button>

    <!-- Expanded content: List of reports -->
    <div
      v-if="isExpanded"
      class="px-3 pb-3 space-y-2 max-h-48 overflow-y-auto"
    >
      <!-- Loading state -->
      <div v-if="isLoading" class="flex items-center justify-center py-8">
        <Loader2 class="h-5 w-5 animate-spin text-muted-foreground" />
        <span class="ml-2 text-sm text-muted-foreground">Loading reports...</span>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="text-center py-8">
        <p class="text-sm text-red-600">{{ error }}</p>
        <Button variant="outline" size="sm" @click="fetchReports" class="mt-3">
          Retry
        </Button>
      </div>

      <!-- Empty state -->
      <div v-else-if="reports.length === 0" class="text-sm text-muted-foreground text-center py-4 bg-background rounded border border-dashed border-border">
        No reports yet
      </div>

      <!-- Reports list -->
      <div v-else class="space-y-2">
        <Card
          v-for="report in reports"
          :key="report.report_uuid"
          @click="goToReport(report.report_uuid)"
          class="border-border shadow-sm cursor-pointer hover:bg-muted"
          :class="{ 'bg-primary/10 border-primary/30': activeReportUuid === report.report_uuid }"
        >
          <CardHeader class="py-3">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2 flex-1 min-w-0">
                <FileText class="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <CardTitle class="text-sm font-medium truncate">
                  {{ report.title }}
                </CardTitle>
              </div>
              <span class="text-xs text-muted-foreground flex-shrink-0 ml-2">v{{ report.version }}</span>
            </div>
          </CardHeader>
        </Card>
      </div>
    </div>
  </div>
</template>
